import React, { type RefObject } from 'react';
import {
  Box,
  Button,
  Stack,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowOutward as ActionIcon,
} from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type { ValidationIssueFamily } from '../lib/validationIssueFamilies';
import ValidationPriorityBadge from './ValidationPriorityBadge';
import SeverityBadge from './SeverityBadge';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { getWorkflowWorkspacePalette } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import WorkspaceSelectableCard from '../../../shared/ui/workflow/WorkspaceSelectableCard';
import type { WorkspaceTone } from '../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import {
  formatCategory,
  resolveFamilyCountLabel,
  resolvePrimaryValidationTitle,
} from '../lib/validationPresentation';
import { BodyText, CaptionText, CardTitle, OverlineText } from '../../../shared/ui/typography';

interface ValidationIssueCardProps {
  family: ValidationIssueFamily;
  displayNumber: number;
  selected: boolean;
  onSelect: () => void;
  onExecutePrimaryAction: (family: ValidationIssueFamily) => void;
  onMoveSelection: (familyId: string, direction: -1 | 1) => void;
  tourCardTestId?: string;
  tourActionTestId?: string;
  tourRefs?: {
    actionRef?: RefObject<HTMLDivElement | null>;
  };
}

const ValidationIssueCard = React.forwardRef<HTMLDivElement, ValidationIssueCardProps>(function ValidationIssueCard({
  family,
  displayNumber,
  selected,
  onSelect,
  onExecutePrimaryAction,
  onMoveSelection,
  tourCardTestId,
  tourActionTestId,
  tourRefs,
}, ref) {
  const theme = useTheme();
  const workspacePalette = getWorkflowWorkspacePalette(theme);
  const tone: WorkspaceTone =
    family.severity === 'critical' || family.severity === 'error'
      ? 'error'
      : family.severity === 'warning'
        ? 'warning'
        : 'info';
  // Simplified metadata for better scanability
  const categoryLabel = formatCategory(family.category, 'short');
  const impactLabel = resolveFamilyCountLabel(family, 'card');
  const primaryValidationTitle = resolvePrimaryValidationTitle(family);
  const summaryText = family.title !== primaryValidationTitle ? family.title : family.summary;
  
  // Priority indicator
  const priorityIndicator = family.isBlocking 
    ? 'BLOCKER' 
    : family.isRequiredOnly 
    ? 'REQUIRED' 
    : null;
  const primaryActionHelp =
    family.primaryAction.type === 'highlight'
      ? validationActionTooltips.highlightAll
      : family.primaryAction.type === 'detail'
        ? validationActionTooltips.openDetailRepair
      : family.primaryAction.type === 'rerun'
        ? validationActionTooltips.runValidation
        : validationActionTooltips.issuePrimaryAction;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      onMoveSelection(family.id, 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      onMoveSelection(family.id, -1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      onExecutePrimaryAction(family);
    }
  };

  return (
    <WorkspaceSelectableCard
      ref={ref}
      dataTestId={tourCardTestId}
      role="option"
      ariaSelected={selected}
      selected={selected}
      tone={tone}
      onSelect={onSelect}
      onKeyDown={handleKeyDown}
    >
      <Stack spacing={selected ? 0.65 : 0.55} sx={{ minWidth: 0, flex: 1 }}>
        {/* Title row with badges */}
        <Stack direction="row" spacing={layoutGap.comfort} alignItems="flex-start" flexWrap="wrap" useFlexGap>
          <Box
            component="span"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {`Validation #${displayNumber} · ${primaryValidationTitle}`}
          </Box>
          <CaptionText
            component="span"
            sx={{
              px: 0.55,
              py: 0.18,
              borderRadius: `${borderRadius.full}px`,
              border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.24 : 0.16)}`,
              backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.78 : 0.2),
              color: workspacePalette.tertiaryText,
              whiteSpace: 'nowrap',
            }}
          >
            Validation #{displayNumber}
          </CaptionText>
          <CardTitle
            sx={{
              color: workspacePalette.headingText,
              minWidth: 0,
              flex: 1,
              display: '-webkit-box',
              WebkitLineClamp: selected ? 2 : 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {primaryValidationTitle}
          </CardTitle>
          <Stack direction="row" spacing={layoutGap.flush} alignItems="center" flexShrink={0}>
            <SeverityBadge severity={family.severity} validationIds={family.validationIds} compact />
            {priorityIndicator && (
              <OverlineText
                component="span"
                sx={{
                  alignSelf: 'center',
                  px: 0.55,
                  py: 0.18,
                  borderRadius: `${borderRadius.full}px`,
                  border: `1px solid ${alpha(
                    family.isBlocking ? theme.palette.error.main : theme.palette.warning.main,
                    0.22
                  )}`,
                  backgroundColor: alpha(
                    family.isBlocking ? theme.palette.error.main : theme.palette.warning.main,
                    0.08
                  ),
                  color: family.isBlocking ? 'error.main' : 'warning.main',
                }}
              >
                {priorityIndicator}
              </OverlineText>
            )}
          </Stack>
        </Stack>

        {/* Summary - one line */}
        <BodyText
          sx={{
            color: selected ? workspacePalette.headingText : workspacePalette.bodyText,
            display: '-webkit-box',
            WebkitLineClamp: selected ? 2 : 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {summaryText}
        </BodyText>

        {/* Footer: metadata on the left, primary action anchored bottom-right */}
        <Stack
          direction="row"
          spacing={layoutGap.comfort}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
            <CaptionText sx={{ color: workspacePalette.tertiaryText }}>
              {categoryLabel} · {impactLabel}
            </CaptionText>
            <ValidationPriorityBadge validationIds={family.validationIds} compact showRank={false} />
          </Stack>
          <Box ref={tourRefs?.actionRef} data-testid={tourActionTestId} sx={{ flexShrink: 0, ml: 'auto' }}>
            <ActionHelpTooltip help={primaryActionHelp}>
              <Button
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                startIcon={<ActionIcon sx={{ fontSize: 16 }} />}
                onClick={(event) => {
                  event.stopPropagation();
                  onExecutePrimaryAction(family);
                }}
                sx={{
                  minWidth: 0,
                  boxShadow: 'none',
                  px: selected ? 1.35 : 1.1,
                  py: selected ? 0.58 : 0.5,
                  minHeight: 34,
                  borderColor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.22 : 0.18),
                  backgroundColor: selected ? undefined : alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.82 : 0.24),
                  ...(selected
                    ? {
                        '&&': {
                          color: theme.palette.primary.contrastText,
                          backgroundColor: theme.palette.primary.dark,
                        },
                        '&&:hover': {
                          backgroundColor: theme.palette.primary.main,
                        },
                        '& .MuiButton-startIcon': {
                          color: 'inherit',
                        },
                      }
                    : {}),
                }}
              >
                {family.primaryAction.label}
              </Button>
            </ActionHelpTooltip>
          </Box>
        </Stack>
      </Stack>
    </WorkspaceSelectableCard>
  );
});

export default React.memo(ValidationIssueCard);
